import { getSupabaseClient } from "./index";
import {
  CUSTOMERS_PAGE_SIZE,
  formatOrderDisplayNumber,
  normalizePhone,
  type CustomerFilter,
  type CustomerListCursor,
  type CustomerOrderSummary,
  type CustomerRecord,
  type CustomerStats,
  type CustomerStatus,
  type CustomerSource,
  type CustomerAddressSource,
} from "@veorest/utils";

export type {
  CustomerRecord,
  CustomerStats,
  CustomerFilter,
  CustomerStatus,
  CustomerSource,
  CustomerOrderSummary,
  CustomerAddressSource,
};

export interface ListCustomersParams {
  tenantId: string;
  search?: string;
  filter?: CustomerFilter;
  cursor?: CustomerListCursor | null;
  limit?: number;
  /** Inclusive lower bound on `created_at` (ISO). */
  createdFrom?: string;
  /** Inclusive upper bound on `created_at` (ISO). */
  createdTo?: string;
}

export interface ListCustomersResult {
  customers: CustomerRecord[];
  nextCursor: CustomerListCursor | null;
}

export class DuplicatePhoneError extends Error {
  existingCustomerId: string;

  constructor(existingCustomerId: string) {
    super("duplicate_phone");
    this.name = "DuplicatePhoneError";
    this.existingCustomerId = existingCustomerId;
  }
}

export async function listCustomers({
  tenantId,
  search = "",
  filter = "all",
  cursor = null,
  limit = CUSTOMERS_PAGE_SIZE,
  createdFrom,
  createdTo,
}: ListCustomersParams): Promise<ListCustomersResult> {
  const supabase = getSupabaseClient();
  let query = supabase
    .from("customers")
    .select("*")
    .eq("tenant_id", tenantId);

  const term = search.trim();
  if (term) {
    const pattern = `%${term}%`;
    query = query.or(`name.ilike.${pattern},phone.ilike.${pattern},email.ilike.${pattern}`);
  }

  if (filter === "vip") query = query.eq("status", "vip");
  if (filter === "blocked") query = query.eq("status", "blocked");
  if (filter === "regular") query = query.eq("status", "regular");
  if (filter === "new") {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 7);
    query = query.gte("created_at", cutoff.toISOString());
  }

  if (createdFrom) query = query.gte("created_at", createdFrom);
  if (createdTo) query = query.lte("created_at", createdTo);

  if (cursor?.lastOrderAt) {
    query = query.or(
      `last_order_at.lt.${cursor.lastOrderAt},and(last_order_at.eq.${cursor.lastOrderAt},id.lt.${cursor.id})`
    );
  } else if (cursor) {
    query = query.is("last_order_at", null).lt("id", cursor.id);
  }

  const byCreated = Boolean(createdFrom || createdTo);
  const ordered = byCreated
    ? query.order("created_at", { ascending: false }).order("id", { ascending: false })
    : query
        .order("last_order_at", { ascending: false, nullsFirst: false })
        .order("id", { ascending: false });

  const { data, error } = await ordered.limit(limit + 1);

  if (error) throw error;

  const rows = (data ?? []) as CustomerRecord[];
  const hasMore = rows.length > limit;
  const page = hasMore ? rows.slice(0, limit) : rows;
  const last = page[page.length - 1];

  return {
    customers: page,
    nextCursor: hasMore && last ? { lastOrderAt: last.last_order_at, id: last.id } : null,
  };
}

export async function fetchAllCustomersForExport(
  params: Omit<ListCustomersParams, "cursor" | "limit">
): Promise<CustomerRecord[]> {
  const all: CustomerRecord[] = [];
  let cursor: CustomerListCursor | null = null;

  for (;;) {
    const { customers, nextCursor } = await listCustomers({ ...params, cursor, limit: 100 });
    all.push(...customers);
    if (!nextCursor) break;
    cursor = nextCursor;
  }

  return all;
}

export async function getCustomerById(id: string): Promise<CustomerRecord | null> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.from("customers").select("*").eq("id", id).maybeSingle();
  if (error) throw error;
  return (data as CustomerRecord | null) ?? null;
}

export async function getCustomerByPhone(
  tenantId: string,
  phone: string
): Promise<CustomerRecord | null> {
  const supabase = getSupabaseClient();
  const normalized = normalizePhone(phone);
  const { data, error } = await supabase
    .from("customers")
    .select("*")
    .eq("tenant_id", tenantId)
    .eq("phone", normalized)
    .maybeSingle();
  if (error) throw error;
  return (data as CustomerRecord | null) ?? null;
}

export async function getCustomerStats(tenantId: string): Promise<CustomerStats> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase.rpc("get_customer_stats", { p_tenant_id: tenantId });
  if (error) throw error;

  const row = Array.isArray(data) ? data[0] : data;
  return {
    new_this_month: row?.new_this_month ?? 0,
    repeat_customers: row?.repeat_customers ?? 0,
    vip_count: row?.vip_count ?? 0,
  };
}

export interface CreateCustomerInput {
  tenantId: string;
  name: string;
  phone: string;
  email?: string | null;
  addressLine?: string | null;
  source?: CustomerSource;
}

function buildAddressPayload(addressLine?: string | null): {
  address_line: string | null;
  address: string | null;
  address_source: CustomerAddressSource | null;
  address_updated_at: string | null;
} {
  const line = addressLine?.trim() || null;
  if (!line) {
    return {
      address_line: null,
      address: null,
      address_source: null,
      address_updated_at: null,
    };
  }
  return {
    address_line: line,
    address: line,
    address_source: "staff",
    address_updated_at: new Date().toISOString(),
  };
}

export async function createCustomer(input: CreateCustomerInput): Promise<CustomerRecord> {
  const existing = await getCustomerByPhone(input.tenantId, input.phone);
  if (existing) throw new DuplicatePhoneError(existing.id);

  const supabase = getSupabaseClient();
  const addressPayload = buildAddressPayload(input.addressLine);
  const { data, error } = await supabase
    .from("customers")
    .insert({
      tenant_id: input.tenantId,
      name: input.name.trim(),
      phone: normalizePhone(input.phone),
      email: input.email?.trim() || null,
      ...addressPayload,
      source: input.source ?? "manual",
      status: "regular",
    })
    .select("*")
    .single();

  if (error) {
    if (error.code === "23505") {
      const dup = await getCustomerByPhone(input.tenantId, input.phone);
      if (dup) throw new DuplicatePhoneError(dup.id);
    }
    throw error;
  }

  return data as CustomerRecord;
}

export interface UpdateCustomerInput {
  id: string;
  name?: string;
  phone?: string;
  email?: string | null;
  addressLine?: string | null;
}

export async function updateCustomer(input: UpdateCustomerInput): Promise<CustomerRecord> {
  const current = await getCustomerById(input.id);
  if (!current) throw new Error("customer_not_found");

  if (input.phone !== undefined) {
    const normalized = normalizePhone(input.phone);
    if (normalized !== current.phone) {
      const existing = await getCustomerByPhone(current.tenant_id, input.phone);
      if (existing && existing.id !== input.id) {
        throw new DuplicatePhoneError(existing.id);
      }
    }
  }

  const supabase = getSupabaseClient();
  const payload: Record<string, string | number | null> = {};

  if (input.name !== undefined) payload.name = input.name.trim();
  if (input.phone !== undefined) payload.phone = normalizePhone(input.phone);
  if (input.email !== undefined) payload.email = input.email?.trim() || null;
  if (input.addressLine !== undefined) {
    Object.assign(payload, buildAddressPayload(input.addressLine));
  }

  const { data, error } = await supabase
    .from("customers")
    .update(payload)
    .eq("id", input.id)
    .select("*")
    .single();

  if (error) {
    if (error.code === "23505" && input.phone !== undefined) {
      const dup = await getCustomerByPhone(current.tenant_id, input.phone);
      if (dup && dup.id !== input.id) throw new DuplicatePhoneError(dup.id);
    }
    throw error;
  }
  return data as CustomerRecord;
}

export async function updateCustomerStatus(
  id: string,
  status: CustomerStatus
): Promise<CustomerRecord> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("customers")
    .update({ status })
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw error;
  return data as CustomerRecord;
}

export async function updateCustomerNotes(id: string, notes: string): Promise<CustomerRecord> {
  const supabase = getSupabaseClient();
  const { data, error } = await supabase
    .from("customers")
    .update({ notes: notes.trim() || null })
    .eq("id", id)
    .select("*")
    .single();
  if (error) throw error;
  return data as CustomerRecord;
}

export async function deleteCustomer(id: string): Promise<void> {
  const supabase = getSupabaseClient();
  const { error } = await supabase.from("customers").delete().eq("id", id);
  if (error) throw error;
}

export async function countCustomers(tenantId: string): Promise<number> {
  const supabase = getSupabaseClient();
  const { count, error } = await supabase
    .from("customers")
    .select("id", { count: "exact", head: true })
    .eq("tenant_id", tenantId);
  if (error) throw error;
  return count ?? 0;
}

export async function listCustomerOrders(
  tenantId: string,
  phone: string,
  limit = 20
): Promise<CustomerOrderSummary[]> {
  const supabase = getSupabaseClient();
  const normalized = normalizePhone(phone);

  const { data: orders, error } = await supabase
    .from("orders")
    .select("id, status, total")
    .eq("tenant_id", tenantId)
    .eq("customer_phone", normalized)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error || !orders?.length) return [];

  const orderIds = orders.map((o) => o.id as string);
  const { data: items, error: itemsError } = await supabase
    .from("order_items")
    .select("order_id")
    .in("order_id", orderIds);

  if (itemsError) {
    return orders.map((order) => ({
      id: order.id as string,
      displayNumber: formatOrderDisplayNumber(order.id as string),
      itemCount: 0,
      status: order.status as string,
      total: Number(order.total ?? 0),
    }));
  }

  const counts = new Map<string, number>();
  for (const row of items ?? []) {
    const id = row.order_id as string;
    counts.set(id, (counts.get(id) ?? 0) + 1);
  }

  return orders.map((order) => ({
    id: order.id as string,
    displayNumber: formatOrderDisplayNumber(order.id as string),
    itemCount: counts.get(order.id as string) ?? 0,
    status: order.status as string,
    total: Number(order.total ?? 0),
  }));
}
